/**
 * Reference links beside a field's label (Pedro 2026-10-13: the IRS page next to US person,
 * the PEP references next to PEP status). One small "opens in a new tab" icon:
 *   - one link  → the icon is the link;
 *   - several   → the icon opens a short menu, each item a link.
 * Links (not form controls), so they work in Read mode too; every one opens in a new tab
 * with rel="noopener noreferrer". The click never reaches the field's <label>.
 */
import { Anchor, Group, Menu, Tooltip } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import type { ReactNode } from 'react';

import type { ReferenceLink } from '../utils/pepLabels';

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

export function ReferenceLinks({ title, links }: { title: string; links: ReferenceLink[] }) {
  if (links.length === 0) return null;
  if (links.length === 1) {
    const hint = `${title}: ${links[0].label} (opens in a new tab)`;
    return (
      <Tooltip label={hint} withArrow>
        <Anchor href={links[0].url} target="_blank" rel="noopener noreferrer" aria-label={hint}
          onClick={stop} style={{ display: 'inline-flex' }}>
          <IconExternalLink size={13} />
        </Anchor>
      </Tooltip>
    );
  }
  const hint = `${title}: ${links.length} reference pages (each opens in a new tab)`;
  // The trigger is a span, not a <button>: Read mode wraps the page in <fieldset disabled>,
  // which disables buttons inside it. preventDefault keeps the click from reaching the
  // field's <label> (which would focus the select instead).
  return (
    <Menu withinPortal position="bottom-start" shadow="md">
      <Menu.Target>
        <Anchor component="span" role="button" tabIndex={0} aria-label={hint} title={hint}
          onClick={(e: React.MouseEvent) => { e.preventDefault(); stop(e); }}
          onKeyDown={(e: React.KeyboardEvent<HTMLSpanElement>) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); }
          }}
          style={{ display: 'inline-flex', cursor: 'pointer' }}>
          <IconExternalLink size={13} />
        </Anchor>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{title}</Menu.Label>
        {links.map((l) => (
          <Menu.Item key={l.url} component="a" href={l.url} target="_blank" rel="noopener noreferrer"
            leftSection={<IconExternalLink size={13} />}>
            {l.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

/** A field label with its reference links: "US person (FATCA) ↗". */
export function LabelWithLinks({ text, title, links }: { text: ReactNode; title: string; links: ReferenceLink[] }) {
  return (
    <Group gap={4} wrap="nowrap" component="span">
      {text}
      <ReferenceLinks title={title} links={links} />
    </Group>
  );
}
